import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, RefreshCw, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { adminLogsApi } from '@/api/adminLogs';
import { tradingApi } from '@/api/trading';
import type { AdminLogFileDto, AdminLogLevel } from '@/api/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import styles from './AdminLogsPage.module.css';

const levelOptions = [
  { value: '', label: 'All levels' },
  { value: 'Verbose', label: 'Verbose' },
  { value: 'Debug', label: 'Debug' },
  { value: 'Information', label: 'Information' },
  { value: 'Warning', label: 'Warning' },
  { value: 'Error', label: 'Error' },
  { value: 'Fatal', label: 'Fatal' },
];

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GiB`;
}

function formatTimestamp(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(value));
}

export default function AdminLogsPage() {
  const [sourceId, setSourceId] = useState(() => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get('source') ?? 'server');
  const gatewayQuery = useQuery({
    queryKey: ['trading', 'gateways'],
    queryFn: ({ signal }) => tradingApi.gateways(signal),
    refetchInterval: 5_000,
  });
  const filesQuery = useQuery({
    queryKey: sourceId === 'server' ? ['admin-logs'] : ['admin-logs', sourceId, 'files'],
    queryFn: ({ signal }) => sourceId === 'server'
      ? adminLogsApi.list(signal)
      : adminLogsApi.gatewayList(sourceId, signal),
    refetchInterval: 5_000,
  });
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [before, setBefore] = useState<number | null>(null);
  const [newerCursors, setNewerCursors] = useState<Array<number | null>>([]);
  const [fileFilter, setFileFilter] = useState('');
  const [searchText, setSearchText] = useState(() => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get('search') ?? '');
  const [level, setLevel] = useState<AdminLogLevel | ''>('');
  const [searchBefore, setSearchBefore] = useState<number | null>(null);
  const [newerSearchCursors, setNewerSearchCursors] = useState<Array<number | null>>([]);
  const debouncedSearch = useDebouncedValue(searchText, 250).trim();
  const filterActive = searchText.trim().length > 0 || level !== '';
  const searchReady = debouncedSearch.length > 0 || level !== '';
  const previousCurrentName = useRef<string | null>(null);
  const logOutputRef = useRef<HTMLPreElement>(null);
  const files = filesQuery.data ?? [];
  const visibleFiles = useMemo(() => filterLogFiles(files, fileFilter), [fileFilter, files]);
  const currentName = files.find((file) => file.isCurrent)?.name ?? null;
  const effectiveSelectedName = selectedName ?? currentName;
  const selectedFile = files.find((file) => file.name === effectiveSelectedName) ?? null;
  const selectedGateway = gatewayQuery.data?.find((gateway) => gateway.id === sourceId) ?? null;
  const sourceKey = sourceId === 'server' ? [] : [sourceId];
  const latestVersion = before === null ? selectedFile?.sizeBytes ?? 0 : 0;

  useEffect(() => {
    const previousCurrent = previousCurrentName.current;
    setSelectedName((selected) => {
      if (!selected || !files.some((file) => file.name === selected)) return currentName;
      if (!filterActive && before === null && previousCurrent && selected === previousCurrent && currentName !== previousCurrent) {
        return currentName;
      }
      return selected;
    });
    previousCurrentName.current = currentName;
  }, [before, currentName, files, filterActive]);

  useEffect(() => {
    previousCurrentName.current = null;
    setSelectedName(null);
    setBefore(null);
    setNewerCursors([]);
    setSearchBefore(null);
    setNewerSearchCursors([]);
  }, [sourceId]);

  useEffect(() => {
    setBefore(null);
    setNewerCursors([]);
    setSearchBefore(null);
    setNewerSearchCursors([]);
  }, [selectedName]);

  useEffect(() => {
    setSearchBefore(null);
    setNewerSearchCursors([]);
  }, [debouncedSearch, level]);

  const chunkQuery = useQuery({
    queryKey: ['admin-logs', ...sourceKey, effectiveSelectedName, 'content', before, latestVersion],
    queryFn: ({ signal }) => sourceId === 'server'
      ? adminLogsApi.getChunk(effectiveSelectedName!, before, signal)
      : adminLogsApi.getGatewayChunk(sourceId, effectiveSelectedName!, before, signal),
    enabled: effectiveSelectedName !== null && !filterActive,
  });
  const searchQuery = useQuery({
    queryKey: ['admin-logs', ...sourceKey, effectiveSelectedName, 'search', debouncedSearch, level, searchBefore],
    queryFn: ({ signal }) => sourceId === 'server'
      ? adminLogsApi.search(
        effectiveSelectedName!, debouncedSearch, level || null, searchBefore, signal,
      )
      : adminLogsApi.gatewaySearch(
        sourceId, effectiveSelectedName!, debouncedSearch, level || null, searchBefore, signal,
      ),
    enabled: effectiveSelectedName !== null && filterActive && searchReady,
  });
  const chunk = chunkQuery.data;
  const searchPage = searchQuery.data;
  const followingLatest = !filterActive && before === null && selectedFile?.isCurrent === true;

  useEffect(() => {
    if (!followingLatest || !chunk || !logOutputRef.current) return;
    logOutputRef.current.scrollTop = logOutputRef.current.scrollHeight;
  }, [chunk, followingLatest]);

  function selectFile(name: string) {
    setBefore(null);
    setNewerCursors([]);
    setSearchBefore(null);
    setNewerSearchCursors([]);
    setSelectedName(name);
  }

  function showOlder() {
    if (!chunk?.hasOlder) return;
    setNewerCursors((items) => [...items, before]);
    setBefore(chunk.startOffset);
  }

  function showNewer() {
    if (newerCursors.length === 0) return;
    setBefore(newerCursors[newerCursors.length - 1]);
    setNewerCursors(newerCursors.slice(0, -1));
  }

  function showLatest() {
    setBefore(null);
    setNewerCursors([]);
  }

  function showOlderResults() {
    if (!searchPage?.hasOlder || searchPage.nextBefore === null) return;
    setNewerSearchCursors((items) => [...items, searchBefore]);
    setSearchBefore(searchPage.nextBefore);
  }

  function showNewerResults() {
    if (newerSearchCursors.length === 0) return;
    setSearchBefore(newerSearchCursors[newerSearchCursors.length - 1]);
    setNewerSearchCursors(newerSearchCursors.slice(0, -1));
  }

  function showLatestResults() {
    setSearchBefore(null);
    setNewerSearchCursors([]);
  }

  function clearFilters() {
    setFileFilter('');
    setSearchText('');
    setLevel('');
    setBefore(null);
    setNewerCursors([]);
    setSearchBefore(null);
    setNewerSearchCursors([]);
    setSelectedName(currentName);
  }

  async function refresh() {
    const requests: Promise<unknown>[] = [filesQuery.refetch(), gatewayQuery.refetch()];
    if (filterActive && searchReady) requests.push(searchQuery.refetch());
    else if (effectiveSelectedName && !filterActive) requests.push(chunkQuery.refetch());
    await Promise.all(requests);
  }

  const refreshing = filesQuery.isFetching
    || (filterActive ? searchQuery.isFetching : chunkQuery.isFetching);
  const searchPending = filterActive && (!searchReady || searchQuery.isLoading);

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <div>
          <h1>System logs</h1>
          <p>Inspect bounded application, gateway service, and filtered MT5 Experts logs or search a complete retained file.</p>
        </div>
        <Button type="button" variant="ghost" onClick={() => void refresh()} disabled={refreshing}>
          <RefreshCw size={15} aria-hidden="true" /> Refresh
        </Button>
      </div>

      <div className={styles.sourceBar}>
        <Select
          label="Log source"
          value={sourceId}
          onChange={(event) => setSourceId(event.target.value)}
          options={[
            { value: 'server', label: 'Application server' },
            ...(gatewayQuery.data ?? []).map((gateway) => ({
              value: gateway.id,
              label: `${gateway.name} · ${gateway.connected ? 'connected' : gateway.enabled ? 'offline' : 'revoked'}${gateway.supportsRemoteLogs ? '' : ' · unsupported'}`,
            })),
          ]}
        />
        <p>{sourceId === 'server'
          ? 'Application server logs stored on this deployment.'
          : gatewaySourceStatus(selectedGateway)}</p>
      </div>

      {filesQuery.error && <Alert variant="danger">{filesQuery.error.message}</Alert>}
      {gatewayQuery.error && <Alert variant="danger">{gatewayQuery.error.message}</Alert>}
      {!filterActive && chunkQuery.error && <Alert variant="danger">{chunkQuery.error.message}</Alert>}
      {filterActive && searchQuery.error && <Alert variant="danger">{searchQuery.error.message}</Alert>}

      <div className={styles.workspace}>
        <aside className={styles.filePanel} aria-label={sourceId === 'server' ? 'Server log files' : 'Gateway log files'}>
          <h2 className={styles.panelTitle}>Daily files</h2>
          <div className={styles.fileFilter}>
            <Input
              label="Filter daily files"
              type="search"
              value={fileFilter}
              onChange={(event) => setFileFilter(event.target.value)}
              placeholder="Filename or date"
            />
          </div>
          <ul className={styles.fileList}>
            {visibleFiles.map((file) => (
              <li key={file.name}>
                <button
                  type="button"
                  className={`${styles.fileButton}${file.name === effectiveSelectedName ? ` ${styles.fileButtonActive}` : ''}`}
                  onClick={() => selectFile(file.name)}
                  aria-pressed={file.name === effectiveSelectedName}
                >
                  <span className={styles.fileName}>{file.name}</span>
                  <span className={styles.fileMeta}>
                    <span>{formatBytes(file.sizeBytes)}</span>
                    {file.isCurrent && <span className={styles.current}>Current</span>}
                    <span>{formatTimestamp(file.lastModifiedUtc)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {!filesQuery.isLoading && files.length === 0 && (
            <p className={styles.empty}>{sourceId === 'server'
              ? 'No server log files are available.'
              : selectedGateway?.supportsRemoteLogs
                ? 'No gateway service or MegaTraderDX MT5 Experts logs have been uploaded yet.'
                : 'This gateway has not advertised remote log support.'}</p>
          )}
          {!filesQuery.isLoading && files.length > 0 && visibleFiles.length === 0 && (
            <p className={styles.empty}>No daily files match this filter.</p>
          )}
        </aside>

        <section className={styles.viewerPanel} aria-label="Log viewer">
          <div className={styles.viewerHeader}>
            <div>
              <h2>{effectiveSelectedName ?? 'Select a log file'}</h2>
              {followingLatest && <p className={styles.status}>Following latest · checks every 5 seconds</p>}
              {filterActive && <p>Filtered results · live follow paused</p>}
              {selectedFile && !followingLatest && !filterActive && <p>Historical window · live follow paused</p>}
            </div>
            {effectiveSelectedName && (
              <a className={styles.downloadButton} href={sourceId === 'server'
                ? adminLogsApi.downloadUrl(effectiveSelectedName)
                : adminLogsApi.gatewayDownloadUrl(sourceId, effectiveSelectedName)} download>
                <Download size={15} aria-hidden="true" /> Download full file
              </a>
            )}
          </div>

          <div className={styles.searchToolbar}>
            <Input
              label="Search log content"
              type="search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Message, correlation ID, or exception"
              maxLength={256}
            />
            <Select
              label="Log level"
              value={level}
              onChange={(event) => setLevel(event.target.value as AdminLogLevel | '')}
              options={levelOptions}
            />
            {(filterActive || fileFilter) && (
              <Button type="button" variant="ghost" onClick={clearFilters}>Clear filters</Button>
            )}
          </div>

          {filterActive ? (
            <div className={styles.searchResults} aria-live="polite" aria-busy={searchPending}>
              {searchPending && <p className={styles.searchEmpty}><Search size={18} aria-hidden="true" /> Searching log records…</p>}
              {!searchPending && searchPage?.records.length === 0 && (
                <p className={styles.searchEmpty}><Search size={18} aria-hidden="true" /> No log records match these filters.</p>
              )}
              {!searchPending && searchPage?.records.map((record) => (
                <article className={styles.searchRecord} key={`${record.startOffset}-${record.endOffset}`}>
                  <div className={styles.recordMeta}>
                    <span className={styles.level} data-level={record.level ?? 'Unknown'}>{record.level ?? 'Unstructured'}</span>
                    <span>{record.timestampUtc ? formatTimestamp(record.timestampUtc) : 'Timestamp unavailable'}</span>
                    <span>Bytes {record.startOffset.toLocaleString()}–{record.endOffset.toLocaleString()}</span>
                  </div>
                  <pre>{record.content}</pre>
                </article>
              ))}
            </div>
          ) : (
            <pre ref={logOutputRef} className={styles.logOutput} tabIndex={0} aria-label="Log content">
              {chunkQuery.isLoading ? 'Loading log content…' : chunk?.content || 'This log window is empty.'}
            </pre>
          )}

          <div className={styles.pager}>
            {filterActive ? (
              <>
                <div className={styles.viewerActions}>
                  <Button type="button" variant="ghost" onClick={showOlderResults} disabled={!searchPage?.hasOlder}>Older results</Button>
                  <Button type="button" variant="ghost" onClick={showNewerResults} disabled={newerSearchCursors.length === 0}>Newer results</Button>
                  <Button type="button" variant="ghost" onClick={showLatestResults} disabled={searchBefore === null}>Latest results</Button>
                </div>
                {searchPage && (
                  <span className={styles.range}>
                    {searchPage.records.length.toLocaleString()} matches in this page
                  </span>
                )}
              </>
            ) : (
              <>
                <div className={styles.viewerActions}>
                  <Button type="button" variant="ghost" onClick={showOlder} disabled={!chunk?.hasOlder}>Older</Button>
                  <Button type="button" variant="ghost" onClick={showNewer} disabled={newerCursors.length === 0}>Newer</Button>
                  <Button type="button" variant="ghost" onClick={showLatest} disabled={before === null}>Latest</Button>
                </div>
                {chunk && (
                  <span className={styles.range}>
                    Bytes {chunk.startOffset.toLocaleString()}–{chunk.endOffset.toLocaleString()} of {chunk.fileSize.toLocaleString()}
                  </span>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function gatewaySourceStatus(gateway: Awaited<ReturnType<typeof tradingApi.gateways>>[number] | null) {
  if (!gateway) return 'Select a configured gateway to inspect its retained logs.';
  if (!gateway.supportsRemoteLogs) return 'The last connected gateway agent does not support remote logs.';
  if (gateway.connected) return 'Connected · gateway service and configured MegaTraderDX MT5 Experts logs share this retained timeline.';
  if (!gateway.enabled) return 'Revoked · showing the last gateway service and MT5 Experts logs retained on the server.';
  return 'Offline · showing the last gateway service and MT5 Experts logs retained on the server.';
}

export function filterLogFiles(files: readonly AdminLogFileDto[], query: string) {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return [...files];
  return files.filter((file) => {
    const fileDate = file.name.match(/^log-(\d{4})(\d{2})(\d{2})/)?.slice(1).join('-') ?? '';
    const modifiedDate = file.lastModifiedUtc.slice(0, 10);
    return file.name.toLocaleLowerCase().includes(normalized)
      || fileDate.includes(normalized)
      || modifiedDate.includes(normalized);
  });
}

function useDebouncedValue(value: string, delayMs: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);
  return debounced;
}
